import React, { useState, useRef, useId } from 'react'; // Ensure useId is imported
import PropTypes from 'prop-types';

/**
 * A simple tooltip component that appears on hover/focus.
 * Requires React 18+ for the useId hook.
 *
 * @param {object} props - Component props.
 * @param {React.ReactNode} props.children - The element that triggers the tooltip.
 * @param {string} props.text - The text content for the tooltip.
 * @param {'top' | 'bottom' | 'left' | 'right'} [props.position='top'] - Tooltip position.
 * @param {string} [props.className] - Optional additional class names for the container div.
 */
const Tooltip = ({ children, text, position = 'top', className = '' }) => {
  const [isVisible, setIsVisible] = useState(false);

  // Generate unique, stable ID for accessibility using useId (React 18+)
  // This MUST be called unconditionally at the top level.
  const tooltipId = useId();

  const showTooltip = () => setIsVisible(true);
  const hideTooltip = () => setIsVisible(false);

  // Determine position classes
  let positionClasses = '';
  switch (position) {
    case 'bottom':
      positionClasses = 'top-full left-1/2 -translate-x-1/2 mt-2';
      break;
    case 'left':
      positionClasses = 'right-full top-1/2 -translate-y-1/2 mr-2';
      break;
    case 'right':
      positionClasses = 'left-full top-1/2 -translate-y-1/2 ml-2';
      break;
    case 'top':
    default:
      positionClasses = 'bottom-full left-1/2 -translate-x-1/2 mb-2';
      break;
  }

  // Don't render tooltip div if text is empty, but keep the trigger element
  if (!text) {
    return <>{children}</>;
  }

  return (
    <div
      className={`relative inline-flex ${className}`}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
      // Associate the trigger with the tooltip content when visible
      aria-describedby={isVisible ? tooltipId : undefined}
    >
      {children}
      {/* Tooltip Content */}
      <div
        role="tooltip"
        id={tooltipId}
        // Use `hidden` attribute when not visible for better accessibility than just opacity-0
        // However, opacity transitions require it to be in the DOM.
        // Using invisible class along with opacity ensures it's not interactable
        // and doesn't take up space visually when hidden.
        className={`
          absolute ${positionClasses} z-30 whitespace-normal
          transform transition-opacity duration-150 ease-in-out
          ${isVisible ? 'opacity-100' : 'opacity-0 invisible'}
          px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-lg shadow-sm dark:bg-gray-700
          max-w-xs
          pointer-events-none
        `}
      >
        {text}
      </div>
    </div>
  );
};

Tooltip.propTypes = {
  children: PropTypes.node.isRequired,
  text: PropTypes.string.isRequired,
  position: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  className: PropTypes.string,
};

export default Tooltip;